import { Typography } from "@/components/text";
import useProfile from "@/hooks/useProfile";
import { cn } from "@/libraries/cn";
import {
	type DiagnosisDraft,
	type DiagnosisFields,
	useCaseClinicalStore,
} from "@/stores/caseClinical";
import type { Case } from "@/types";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MessageOutlinedIcon from "@mui/icons-material/MessageOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import CaseAskHemiProfileCard from "./CaseAskHemiProfileCard";

interface CaseDiagnosisPanelProps {
	caseItem: Case;
}

export const CaseDiagnosisPanel = memo(
	({ caseItem }: CaseDiagnosisPanelProps) => {
		const { t } = useTranslation("common");
		const { profile } = useProfile();
		const currentUserName = profile?.fullname || "Clinician";
		const caseId = String(caseItem?.id || "");

		const dxFields = useMemo<
			{ k: keyof DiagnosisFields; label: string; req?: boolean }[]
		>(
			() => [
				{ k: "dx", label: t("clinicalDiagnosis.fields.dx"), req: true },
				{ k: "site", label: t("clinicalDiagnosis.fields.site"), req: true },
				{ k: "grade", label: t("clinicalDiagnosis.fields.grade") },
				{ k: "onset", label: t("clinicalDiagnosis.fields.onset") },
				{ k: "infection", label: t("clinicalDiagnosis.fields.infection") },
				{ k: "comorb", label: t("clinicalDiagnosis.fields.comorb") },
			],
			[t],
		);

		const clinicalData = useCaseClinicalStore((s) => s.cases[caseId]);
		const updateDiagnosisField = useCaseClinicalStore(
			(s) => s.updateDiagnosisField,
		);
		const confirmDiagnosis = useCaseClinicalStore((s) => s.confirmDiagnosis);
		const discardDiagnosisDraft = useCaseClinicalStore(
			(s) => s.discardDiagnosisDraft,
		);

		const dxRecord = clinicalData?.dxRecord ?? null;
		const dxDrafts = clinicalData?.dxDrafts ?? [];
		const dxHistory = clinicalData?.dxHistory ?? [];

		// Expand/collapse states for cards
		const [openCards, setOpenCards] = useState<Record<string, boolean>>({});
		// Field currently being edited: { cardId: string, key: keyof DiagnosisFields }
		const [editing, setEditing] = useState<{
			cardId: string;
			key: keyof DiagnosisFields;
		} | null>(null);
		const [editValue, setEditValue] = useState("");
		const [historyOpen, setHistoryOpen] = useState(false);

		const isOpen = (uid: string, defaultOpen: boolean) => {
			return openCards[uid] !== undefined ? openCards[uid] : defaultOpen;
		};

		const toggleCard = (uid: string, defaultOpen: boolean) => {
			setOpenCards((prev) => ({
				...prev,
				[uid]: !isOpen(uid, defaultOpen),
			}));
		};

		const handleShowSource = (srcId?: string) => {
			let el: HTMLElement | null = null;
			if (srcId) {
				el =
					document.getElementById(`msg-${srcId}`) ||
					document.querySelector(`[data-message-id="${srcId}"]`) ||
					document.querySelector(`[id*="${srcId}"]`);
			}
			if (!el) {
				// Fallback to finding the tagged diagnosis message in the timeline
				el =
					document.querySelector('[data-tag="diagnosis"]') ||
					document.querySelector('[data-tag="DIAGNOSIS"]') ||
					document.querySelector('.message-box[data-tag*="diag"]');
			}
			if (!el) {
				// Look for any message-box with a Diagnosis badge
				const allBadges = Array.from(
					document.querySelectorAll(
						".message-box .badge, .message-box span, .message-box p",
					),
				);
				const diagBadge = allBadges.find(
					(b) => b.textContent?.trim().toLowerCase() === "diagnosis",
				);
				if (diagBadge) {
					el =
						(diagBadge.closest(".message-box") as HTMLElement) ||
						diagBadge.parentElement;
				}
			}
			if (!el) {
				const amber = document.querySelector(".bg-amber-400");
				if (amber) {
					el =
						(amber.closest(".message-box") as HTMLElement) ||
						amber.parentElement;
				}
			}
			if (!el) {
				el = document.querySelector(".message-box") as HTMLElement;
			}

			const outerContainer = document.querySelector(
				".overflow-y-auto",
			) as HTMLElement | null;

			if (el) {
				if (outerContainer) {
					const containerRect = outerContainer.getBoundingClientRect();
					const elRect = el.getBoundingClientRect();
					const targetScrollTop =
						elRect.top - containerRect.top + outerContainer.scrollTop;
					outerContainer.scrollTo({
						top: targetScrollTop - 40,
						behavior: "smooth",
					});
				}
				el.scrollIntoView({ behavior: "smooth", block: "center" });
				const targetPanel =
					(el.querySelector(".MuiCard-root") as HTMLElement) ||
					(el.querySelector('[class*="MuiCard"]') as HTMLElement) ||
					el;
				targetPanel.classList.add(
					"ring-4",
					"ring-purple-500",
					"ring-offset-2",
					"transition-all",
					"duration-300",
				);
				setTimeout(() => {
					targetPanel.classList.remove(
						"ring-4",
						"ring-purple-500",
						"ring-offset-2",
					);
				}, 2500);
			} else if (outerContainer) {
				const mainEl = document.querySelector("main");
				if (mainEl) {
					const containerRect = outerContainer.getBoundingClientRect();
					const mainRect = mainEl.getBoundingClientRect();
					outerContainer.scrollTo({
						top: mainRect.top - containerRect.top + outerContainer.scrollTop,
						behavior: "smooth",
					});
				}
				toast.info(
					srcId
						? `Source message #${srcId} in timeline`
						: "Source message in timeline below",
				);
			}
		};

		const handleStartEdit = (
			cardId: string,
			key: keyof DiagnosisFields,
			currentVal: string,
		) => {
			setEditing({ cardId, key });
			setEditValue(currentVal || "");
		};

		const handleSaveEdit = (cardId: string, key: keyof DiagnosisFields) => {
			updateDiagnosisField(
				caseId,
				cardId,
				key,
				editValue.trim(),
				currentUserName,
			);
			setEditing(null);
			if (cardId === "rec") {
				toast.success(
					`Saved as v${(dxRecord?.version ?? 1) + 1}. Previous version moved to history.`,
				);
			}
		};

		const handleConfirmDraft = (draft: DiagnosisDraft) => {
			if (!draft.f.dx || !draft.f.site) {
				toast.error(
					"Primary diagnosis and Site are required before confirming.",
				);
				return;
			}
			const success = confirmDiagnosis(caseId, draft.uid, currentUserName);
			if (success) {
				toast.success("Confirmed diagnosis saved against this case.");
				setOpenCards((prev) => ({ ...prev, [draft.uid]: false }));
			}
		};

		const handleDiscardDraft = (uid: string) => {
			discardDiagnosisDraft(caseId, uid);
			toast.info(
				"Draft discarded. The tagged message remains in the timeline.",
			);
		};

		const hasAnyDiagnosis = Boolean(dxRecord || dxDrafts.length > 0);

		return (
			<div className="shrink-0 flex flex-col">
				<div className="w-full bg-[#f4f7fa] border-b border-zinc-200 py-3">
					<div className="container max-w-3xl w-full flex flex-col gap-3 px-4">
						{/* Empty state when no diagnosis at all */}
						{!hasAnyDiagnosis ? (
							<div className="rounded-xl border border-zinc-200 bg-white py-8 px-6 text-center shadow-sm flex flex-col items-center justify-center">
								<div className="mb-3.5 flex h-14 w-14 items-center justify-center rounded-full bg-sky-50 text-sky-600">
									<MessageOutlinedIcon sx={{ fontSize: 26 }} />
								</div>
								<Typography
									fontWeight={700}
									fontSize={16}
									align="center"
									className="text-zinc-800 text-center"
								>
									{t("clinicalDiagnosis.emptyTitle")}
								</Typography>
								<Typography
									fontSize={13}
									align="center"
									className="mt-1.5 text-zinc-500 max-w-md mx-auto text-center leading-relaxed"
								>
									{t("clinicalDiagnosis.emptyDesc")}
								</Typography>
							</div>
						) : (
							<div className="flex flex-col gap-3">
								<div className="flex items-center justify-between">
									<span className="text-xs font-bold uppercase tracking-wider text-zinc-500">
										{t("clinicalDiagnosis.summaryTitle")}{" "}
										<span className="text-xs font-normal normal-case text-zinc-400">
											{t("clinicalDiagnosis.summarySubtitle")}
										</span>
									</span>
									{dxHistory.length > 0 ? (
										<button
											type="button"
											onClick={() => setHistoryOpen(true)}
											className="flex items-center gap-1 text-xs font-semibold text-sky-600 hover:text-sky-700"
										>
											<HistoryRoundedIcon fontSize="small" />
											{t("clinicalDiagnosis.history")} ({dxHistory.length})
										</button>
									) : null}
								</div>

								{/* Confirmed Diagnosis Card */}
								{dxRecord ? (
									<div
										className={cn(
											"rounded-xl border bg-white p-4 shadow-sm transition-all",
											"border-emerald-200",
										)}
									>
										{/* Card Header */}
										<button
											type="button"
											onClick={() => toggleCard(dxRecord.uid, false)}
											className="w-full flex cursor-pointer items-center justify-between select-none text-left"
										>
											<span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
												<CheckIcon fontSize="inherit" className="stroke-[3]" />
												{t("clinicalDiagnosis.confirmed")} · v{dxRecord.version}
											</span>
											<span className="text-zinc-400">
												{isOpen(dxRecord.uid, false) ? (
													<KeyboardArrowUpIcon fontSize="small" />
												) : (
													<KeyboardArrowDownIcon fontSize="small" />
												)}
											</span>
										</button>

										{/* Folded view */}
										{!isOpen(dxRecord.uid, false) ? (
											<div className="mt-2.5">
												<Typography
													fontSize={14}
													fontWeight={700}
													className="text-zinc-900"
												>
													{dxRecord.f.dx || "No diagnosis stated"}
													{dxRecord.f.site ? ` · ${dxRecord.f.site}` : ""}
													{dxRecord.f.grade ? ` · ${dxRecord.f.grade}` : ""}
												</Typography>
												<Typography
													fontSize={11.5}
													className="mt-1 text-zinc-400"
												>
													Confirmed by {dxRecord.by} · {dxRecord.at}
												</Typography>
											</div>
										) : (
											/* Expanded view */
											<div className="mt-3 divide-y divide-zinc-100">
												<Typography
													fontSize={11.5}
													className="pb-2 text-zinc-500"
												>
													{t("clinicalDiagnosis.fromAuthor", {
														author: dxRecord.author,
													})}
												</Typography>

												{dxFields.map((f) => {
													const val = dxRecord.f[f.k];
													const isEditingThis =
														editing?.cardId === "rec" && editing?.key === f.k;

													return (
														<div
															key={f.k}
															className="flex items-start justify-between py-2.5 text-sm gap-2"
														>
															<div className="flex-1 min-w-0">
																<span className="block text-xs font-medium text-zinc-500 mb-0.5">
																	{f.label}
																	{f.req ? (
																		<b className="text-red-500 ml-0.5">*</b>
																	) : null}
																</span>
																{isEditingThis ? (
																	<div className="mt-1 flex items-center gap-2">
																		<input
																			type="text"
																			value={editValue}
																			ref={(el) => el?.focus()}
																			onChange={(e) =>
																				setEditValue(e.target.value)
																			}
																			onKeyDown={(e) => {
																				if (e.key === "Enter") {
																					handleSaveEdit("rec", f.k);
																				} else if (e.key === "Escape") {
																					setEditing(null);
																				}
																			}}
																			className="flex-1 rounded-lg border border-sky-500 px-2.5 py-1 text-sm outline-none bg-sky-50/20"
																		/>
																		<button
																			type="button"
																			onClick={() => handleSaveEdit("rec", f.k)}
																			className="rounded-lg bg-sky-600 px-3 py-1 text-xs font-bold text-white hover:bg-sky-700"
																		>
																			{t("save")}
																		</button>
																		<button
																			type="button"
																			onClick={() => setEditing(null)}
																			className="rounded-lg border border-zinc-200 px-2 py-1 text-xs font-semibold text-zinc-600"
																		>
																			{t("cancel")}
																		</button>
																	</div>
																) : (
																	<>
																		<span
																			className={cn(
																				"text-sm font-semibold break-words",
																				val
																					? "text-zinc-800"
																					: "text-zinc-400 italic font-normal",
																			)}
																		>
																			{val || t("clinicalDiagnosis.notStated")}
																		</span>
																		{val ? (
																			<button
																				type="button"
																				onClick={() =>
																					handleShowSource(dxRecord.srcId)
																				}
																				className="mt-1.5 flex items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5 text-[10.5px] font-semibold text-purple-700 hover:bg-purple-100"
																			>
																				<MessageOutlinedIcon
																					style={{ fontSize: 11 }}
																				/>
																				{t(
																					"clinicalDiagnosis.fromTaggedMessage",
																				)}{" "}
																				· {dxRecord.srcWhen || "today"}
																			</button>
																		) : null}
																	</>
																)}
															</div>

															{!isEditingThis ? (
																<button
																	type="button"
																	onClick={() =>
																		handleStartEdit("rec", f.k, val)
																	}
																	className="text-sky-600 hover:text-sky-700 p-1 flex-none"
																	title={t("clinicalDiagnosis.editField")}
																>
																	<EditOutlinedIcon fontSize="small" />
																</button>
															) : null}
														</div>
													);
												})}

												<div className="pt-3 text-xs text-zinc-500">
													{t("clinicalDiagnosis.confirmedBy", {
														author: dxRecord.by,
														time: dxRecord.at,
														version: dxRecord.version,
													})}
													{dxHistory.length > 0 ? (
														<>
															{" "}
															·{" "}
															<button
																type="button"
																onClick={() => setHistoryOpen(true)}
																className="text-sky-600 underline font-semibold"
															>
																{t("clinicalDiagnosis.viewHistory")}
															</button>
														</>
													) : null}
												</div>
											</div>
										)}
									</div>
								) : null}

								{/* Draft Cards */}
								{dxDrafts.map((draft) => {
									const open = isOpen(draft.uid, true);
									const hasDifferenceWithRecord =
										dxRecord &&
										(draft.f.dx !== dxRecord.f.dx ||
											draft.f.site !== dxRecord.f.site ||
											draft.f.grade !== dxRecord.f.grade);
									const canConfirm = Boolean(draft.f.dx && draft.f.site);

									return (
										<div
											key={draft.uid}
											className="rounded-xl border-2 border-dashed border-amber-400 bg-[#fffdf9] p-4 shadow-sm"
										>
											{/* Card Header */}
											<button
												type="button"
												onClick={() => toggleCard(draft.uid, true)}
												className="w-full flex cursor-pointer items-center justify-between select-none text-left"
											>
												<span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-800">
													<WarningAmberRoundedIcon fontSize="inherit" />
													Draft — not confirmed
												</span>
												<span className="text-zinc-400">
													{open ? (
														<KeyboardArrowUpIcon fontSize="small" />
													) : (
														<KeyboardArrowDownIcon fontSize="small" />
													)}
												</span>
											</button>

											{!open ? (
												<div className="mt-2.5">
													<Typography
														fontSize={14}
														fontWeight={700}
														className="text-zinc-900"
													>
														{draft.f.dx || "No diagnosis stated"}
														{draft.f.site ? ` · ${draft.f.site}` : ""}
														{draft.f.grade ? ` · ${draft.f.grade}` : ""}
													</Typography>
													<Typography
														fontSize={11.5}
														className="mt-1 text-zinc-400"
													>
														From {draft.author}
													</Typography>
												</div>
											) : (
												<div className="mt-3">
													{/* Conflict warning if differs from confirmed version */}
													{hasDifferenceWithRecord ? (
														<div className="mb-3 flex items-start gap-2 rounded-lg bg-red-50 p-2.5 text-xs text-red-800 border border-red-200">
															<ErrorOutlineRoundedIcon
																fontSize="small"
																className="flex-none mt-0.5 text-red-600"
															/>
															<span>
																Different from confirmed v{dxRecord.version}.
																Confirming this replaces it; v{dxRecord.version}{" "}
																stays in history.
															</span>
														</div>
													) : null}

													<Typography
														fontSize={11.5}
														className="pb-2 text-zinc-500"
													>
														{t("clinicalDiagnosis.fromAuthor", {
															author: draft.author,
														})}
													</Typography>

													<div className="divide-y divide-zinc-100">
														{dxFields.map((f) => {
															const val = draft.f[f.k];
															const isEditingThis =
																editing?.cardId === draft.uid &&
																editing?.key === f.k;

															return (
																<div
																	key={f.k}
																	className="flex items-start justify-between py-2.5 text-sm gap-2"
																>
																	<div className="flex-1 min-w-0">
																		<span className="block text-xs font-medium text-zinc-500 mb-0.5">
																			{f.label}
																			{f.req ? (
																				<b className="text-red-500 ml-0.5">*</b>
																			) : null}
																		</span>
																		{isEditingThis ? (
																			<div className="mt-1 flex items-center gap-2">
																				<input
																					type="text"
																					value={editValue}
																					ref={(el) => el?.focus()}
																					onChange={(e) =>
																						setEditValue(e.target.value)
																					}
																					onKeyDown={(e) => {
																						if (e.key === "Enter") {
																							handleSaveEdit(draft.uid, f.k);
																						} else if (e.key === "Escape") {
																							setEditing(null);
																						}
																					}}
																					className="flex-1 rounded-lg border border-amber-500 px-2.5 py-1 text-sm outline-none bg-amber-50/20"
																				/>
																				<button
																					type="button"
																					onClick={() =>
																						handleSaveEdit(draft.uid, f.k)
																					}
																					className="rounded-lg bg-amber-600 px-3 py-1 text-xs font-bold text-white hover:bg-amber-700"
																				>
																					{t("save")}
																				</button>
																				<button
																					type="button"
																					onClick={() => setEditing(null)}
																					className="rounded-lg border border-zinc-200 px-2 py-1 text-xs font-semibold text-zinc-600"
																				>
																					{t("cancel")}
																				</button>
																			</div>
																		) : (
																			<>
																				<span
																					className={cn(
																						"text-sm font-semibold break-words",
																						val
																							? "text-zinc-800"
																							: "text-zinc-400 italic font-normal",
																					)}
																				>
																					{val ||
																						t("clinicalDiagnosis.notStated")}
																				</span>
																				{val ? (
																					<button
																						type="button"
																						onClick={() =>
																							handleShowSource(draft.srcId)
																						}
																						className="mt-1.5 flex items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5 text-[10.5px] font-semibold text-purple-700 hover:bg-purple-100"
																					>
																						<MessageOutlinedIcon
																							style={{ fontSize: 11 }}
																						/>
																						{t(
																							"clinicalDiagnosis.fromTaggedMessage",
																						)}{" "}
																						· {draft.srcWhen || "today"}
																					</button>
																				) : null}
																			</>
																		)}
																	</div>

																	{!isEditingThis ? (
																		<button
																			type="button"
																			onClick={() =>
																				handleStartEdit(draft.uid, f.k, val)
																			}
																			className="text-amber-600 hover:text-amber-700 p-1 flex-none"
																			title={t("clinicalDiagnosis.editField")}
																		>
																			<EditOutlinedIcon fontSize="small" />
																		</button>
																	) : null}
																</div>
															);
														})}
													</div>

													{/* Action Buttons */}
													<div className="mt-4 flex items-center gap-2">
														<button
															type="button"
															onClick={() => handleDiscardDraft(draft.uid)}
															className="flex-1 rounded-lg border border-zinc-300 py-2 text-xs font-bold text-zinc-700 hover:bg-zinc-50"
														>
															{t("clinicalDiagnosis.discard")}
														</button>
														<button
															type="button"
															disabled={!canConfirm}
															onClick={() => handleConfirmDraft(draft)}
															className={cn(
																"flex-1 rounded-lg py-2 text-xs font-bold text-white transition-colors",
																canConfirm
																	? "bg-sky-600 hover:bg-sky-700"
																	: "bg-zinc-300 cursor-not-allowed",
															)}
														>
															{t("clinicalDiagnosis.confirm")}
														</button>
													</div>
													{!canConfirm ? (
														<p className="mt-1.5 text-center text-[11px] text-zinc-400">
															{t("clinicalDiagnosis.reqNote")}
														</p>
													) : null}
												</div>
											)}
										</div>
									);
								})}
							</div>
						)}

						{/* AskHEMI Profile Card (Read-only synced data) */}
						<CaseAskHemiProfileCard caseItem={caseItem} />
					</div>
				</div>

				{/* History Dialog */}
				<Dialog
					open={historyOpen}
					onClose={() => setHistoryOpen(false)}
					maxWidth="xs"
					fullWidth
				>
					<DialogTitle className="flex items-center justify-between border-b pb-3 text-base font-bold">
						Diagnosis History
						<IconButton size="small" onClick={() => setHistoryOpen(false)}>
							<CloseIcon fontSize="small" />
						</IconButton>
					</DialogTitle>
					<DialogContent className="pt-4 divide-y">
						{dxHistory.length === 0 ? (
							<p className="text-sm text-zinc-400 py-4 text-center">
								No previous versions.
							</p>
						) : (
							[...dxHistory].reverse().map((h) => (
								<div key={h.version} className="py-3 text-sm">
									<div className="flex items-center justify-between mb-1">
										<span className="font-bold text-zinc-900">
											Version {h.version}
										</span>
										<span className="text-xs text-zinc-400">{h.at}</span>
									</div>
									<div className="text-zinc-700">
										<p className="font-semibold">{h.f.dx || "-"}</p>
										<p className="text-xs text-zinc-500">
											{h.f.site} {h.f.grade ? `· ${h.f.grade}` : ""}
										</p>
									</div>
									<p className="text-[11px] text-zinc-400 mt-1">
										Confirmed by {h.by}
									</p>
								</div>
							))
						)}
					</DialogContent>
				</Dialog>
			</div>
		);
	},
);

CaseDiagnosisPanel.displayName = "CaseDiagnosisPanel";
export default CaseDiagnosisPanel;
